import * as React from 'react';
import { graphql, ScriptStrategy, useStaticQuery } from 'gatsby';
import Script from 'analytics/components/Script';
import type { YellowAIChatbotSettings, Maybe } from '@types/gatsby-types';

export const yellowAISettingsQuery = graphql`
    query YellowAIChatbotQuery {
        settings {
            yellowai {
                enabled
                id
            }
        }
    }
`;

export type YellowAIChatbotQuery = {
    settings: Maybe<{
        yellowai: Maybe<YellowAIChatbotSettings>;
    }>;
};

type YellowAIChatbotProps = {
    isCMS: boolean;
};

export default function YellowAIChatbot({ isCMS }: YellowAIChatbotProps) {
    const { settings } = useStaticQuery<YellowAIChatbotQuery>(
        yellowAISettingsQuery,
    );

    const yellow = settings?.yellowai;
    const botId = yellow?.id;
    const enabled = yellow?.enabled;

    const ymConfig = { bot: botId, host: 'https://r4.cloud.yellow.ai' };

    // don’t render if in CMS or if it’s disabled or missing an ID
    if (isCMS || !enabled || !botId) {
        return null;
    }

    // script given by yellow AI team
    const inlineScript = `
  function createLink(text, href, title) {
    const link = document.createElement("a");
    link.href = href;
    link.target = "_blank";
    link.style.textDecoration = "underline";
    link.style.display = "inline-block";
    link.style.margin = "0 8px";
    link.style.color = "#888e91";
    link.style.fontSize = "14px"; // Increased font size for links
    link.title = title;
    link.textContent = text;
    return link;
  }

  function changeBotBranding() {
    const frameDocument = window.frames?.ymIframe?.document;
    const botBranding = frameDocument?.getElementById("ymFramebranding");

    if (!botBranding) return;

    botBranding.innerHTML = "";

    const brandingContainer = document.createElement("div");

    // Center-aligned links
    const linksWrapper = document.createElement("div");
    linksWrapper.style.textAlign = "center";
    linksWrapper.appendChild(
      createLink("Privacy Policy", "https://www.bondvet.com/privacy", "Privacy Policy")
    );
    linksWrapper.appendChild(
      createLink("Terms and Conditions", "https://bondvet.com/terms-of-use", "Terms and Conditions")
    );

    // Center-aligned disclaimer text
    const brandingContainerText = document.createElement("div");
    brandingContainerText.textContent =
      "AI responses are for informational use only and are not veterinary medical advice";
    brandingContainerText.style.fontStyle = "italic";
    brandingContainerText.style.marginTop = "6px";
    brandingContainerText.style.textAlign = "center";
    brandingContainerText.style.fontSize = "14px"; // Increased font size for text

    brandingContainer.appendChild(linksWrapper);
    brandingContainer.appendChild(brandingContainerText);
    botBranding.appendChild(brandingContainer);
  }

  window.addEventListener("message", (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data?.event_code === "create-ui") {
        setTimeout(changeBotBranding, 0);
      }
    } catch (e) {
      // Fail silently
    }
  });

  window.ymConfig = ${JSON.stringify(ymConfig)};

  (function () {
    const w = window;
    const ic = w.YellowMessenger;

    if (typeof ic === "function") {
      ic("reattach_activator");
      ic("update", ymConfig);
    } else {
      const d = document;

      const i = function () {
        i.c(arguments);
      };
      i.q = [];
      i.c = function (args) {
        i.q.push(args);
      };

      w.YellowMessenger = i;

      const loadScript = function () {
        const script = d.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = "https://cdn.yellowmessenger.com/plugin/widget-v2/latest/dist/main.min.js";
        const firstScript = d.getElementsByTagName("script")[0];
        firstScript.parentNode.insertBefore(script, firstScript);
      };

      if (w.attachEvent) {
        w.attachEvent("onload", loadScript);
      } else {
        w.addEventListener("load", loadScript, false);
      }
    }
  })();
`;

    return (
        <>
            <Script
                strategy={ScriptStrategy.idle}
                cookieType="necessary"
                content={inlineScript}
            />
            <Script
                strategy={ScriptStrategy.idle}
                cookieType="necessary"
                id="__yellow"
                src="https://cdn.yellowmessenger.com/plugin/widget-v2/latest/dist/main.min.js"
            />
        </>
    );
}
